import React, { useEffect, useContext, useRef } from 'react';
import { useNavigate  } from 'react-router-dom';
import { usePriceContext } from '../Components/Context/PriceContext';
import { useUserContext } from '../../UserContext'; 
import axiosInstance from '../../axiosInstance';
import ReactDOM from 'react-dom';
import './PaymentModal.css';

const API_URL = process.env.REACT_APP_API_URL;

const PaymentModal = React.forwardRef(
  ({ paymentData, amount, onClose }, ref) => {
    const { setConfirmation, 
      setPaymentSuccess, 
      message, setMessage, 
      email,
      activeMessageId, setActiveMessageId,
      activeBalanceDue, setActiveBalanceDue } = usePriceContext(); 
    const { user } = useUserContext();
    const paypalContainerRef = useRef(null);
    const userEmail = message.length > 0 ? message[0].email : email; // Use message email if available, otherwise fallback to context email
    const userMessage = message.length > 0 ? message[0]._id : null; 
    const navigate = useNavigate();


  const updateLocalBalanceDue = () => {
    const updatedMessages = message.map(msg => {
      if (msg._id === activeMessageId) {
          return { ...msg, balanceDue: 0 };
      }
      return msg;
    });
    setMessage(updatedMessages);
    setActiveBalanceDue(0); // Also update the global state
  };

  const recordPaymentReceipt = async (details) => {
    try {
      const givenName = details.payer?.name?.given_name || 'Unknown';
      const surname = details.payer?.name?.surname || '';
      const paypalClient = `${givenName} ${surname}`.trim();
      
      const userRole = user?.role || sessionStorage.getItem('userRole') || 'professional';
      const orderId = activeMessageId || 'New Project Request';
      const numericAmount = paymentData.numericAmount;
      const discountedTotalCost = paymentData.discountedTotalCost || paymentData.coupons; // Use proper field
      const origin = window.location.pathname;
      const subdomain = window.location.hostname;
      const formData = paymentData.formData || paymentData.documentType; // Better data structure

      await axiosInstance.post(`${API_URL}/api/data/capture-payment-modal`, {
        paypalClient: paypalClient,
        userEmail: paymentData.email,
        userRole: userRole,
        orderId: orderId,
        formData: formData,
        amount: numericAmount,
        discountedTotalCost: discountedTotalCost, // Correct spelling
        paypalTransactionId: details.id,
        origin: origin,
        subdomain: subdomain,
        status: 'completed'
      });
      console.log('Payment receipt recorded');
    } catch (error) {
      console.error('Failed to record receipt (non-critical):', error);
    }
  };
  

  useEffect(() => {
    if (!amount || !window.paypal || !paypalContainerRef.current) return;
    const buttons = window.paypal.Buttons({
      createOrder: (data, actions) => {
        return actions.order.create({
          purchase_units: [{
            amount: {
              value: amount.toString(),
            },
          }],
        });
      },
      onApprove: (data, actions) => {
        if (!message.length) {
          // No message found, simply proceed without updating the balance due
          return actions.order.capture().then(async (details) => { 
            await recordPaymentReceipt(details);
            alert(`Transaction completed by ${details.payer.name.given_name}`);
            setConfirmation(true);
            setPaymentSuccess(true);
            onClose();
          });
        } else {
          // Message exists, proceed with updating balance due
          return actions.order.capture().then(async (details) => { 
            await recordPaymentReceipt(details);
            alert(`Transaction completed by ${details.payer.name.given_name}`);
            setConfirmation(true);
            setPaymentSuccess(true);
            updateBalanceDue(userEmail, activeMessageId);

            onClose();
          });
        }
      },
      onCancel: () => {
        alert('Payment was canceled.');
        setConfirmation(false);
        onClose();
       
      },
      onError: (err) => {
        console.error('PayPal Checkout error:', err);
        alert('An error occurred. Please try again.');
        setConfirmation(false);
        onClose();
      },
    });

    if (paypalContainerRef.current.childNodes.length === 0) {
      buttons.render(paypalContainerRef.current);
    }

    return () => {
      if (paypalContainerRef.current) {
        paypalContainerRef.current.innerHTML = '';
      }
    };
  }, [amount, email, paymentData, onClose, setConfirmation, user,]);

  const updateBalanceDue = async (userEmail, activeMessageId) => {
    try {
      updateLocalBalanceDue();  // Update the UI immediately
      const response = await axiosInstance.post(`${API_URL}/api/deliverable/update-balance-due`, {
        email: userEmail,
        orderId: activeMessageId,
      });
    } catch (error) {
      console.error('Error updating balance due:', error);
    }
  };
    

  const testPaymentSuccess = () => {
    // Create mock PayPal details for testing
    const mockDetails = {
      id: 'TEST_' + Date.now(), // Unique test transaction ID
      payer: {
        name: {
          given_name: 'Test',
          surname: 'User'
        }
      }
    };

    // Simulate the payment receipt recording
    const simulatePayment = async () => {
      try {
        await recordPaymentReceipt(mockDetails);
        
        if (!message.length) {
          // No message found, simply proceed without updating the balance due
          setConfirmation(true);
          setPaymentSuccess(true);
          // In testPaymentSuccess, instead of generic alert:
          alert(`Test transaction completed by ${mockDetails.payer.name.given_name}`);
        } else {
          // If message exists, update balance due
          updateBalanceDue(userEmail, activeMessageId);
          setConfirmation(true);
          setPaymentSuccess(true);
          alert('Test transaction completed successfully');
        }
      } catch (error) {
        console.error('Test payment failed:', error);
        alert('Test payment simulation failed');
      } finally {
        onClose();
      }
    };

    // Execute the test payment
    simulatePayment();
  };
    

  return ReactDOM.createPortal(
    <div className="modal-overlay">
      <div className="modal-content">
        <h4></h4>
        <h4>${paymentData.amount}</h4>
        <div id="paypal-container" ref={paypalContainerRef} className="paypal-buttons" />
          <button onClick={() => {
          if (process.env.REACT_APP_ENABLE_TEST_PAYMENTS === 'true') {
            // In development, Cancel button triggers test payment
            testPaymentSuccess();
          } else {
            // In production, normal cancel behavior
            onClose();
          }
        }}>
          {process.env.REACT_APP_ENABLE_TEST_PAYMENTS === 'true' ? 'Test Payment' : 'Cancel Payment'}
        </button>
      </div>
    </div>,
    document.body
  );
});

PaymentModal.displayName = 'PaymentModal';
export default PaymentModal;